import React, { useState } from "react";
import { Box, Typography, Paper, IconButton, Collapse, Button, Chip } from "@mui/material";
import PetsIcon from "@mui/icons-material/Pets";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { QuizStyledCard } from "../../shared/QuizStyledCard";
import { COLORS } from "../constants";
import type { DogProfileExtended } from "../types";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  dogProfiles: DogProfileExtended[];
  onEditDog?: (dog: DogProfileExtended) => void;
  onDeleteDog?: (dog: DogProfileExtended) => void;
  onRestoreDog?: (dog: DogProfileExtended) => void;
}

export function DogProfilesSection({ dogProfiles, onEditDog, onDeleteDog, onRestoreDog }: Props) {
  const [show, setShow] = useState(true);

  if (dogProfiles.length === 0) return null;

  return (
    <QuizStyledCard>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setShow(!show)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PetsIcon sx={{ color: COLORS.brown }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: COLORS.brownDeep }}>
            Dog Profiles ({dogProfiles.length})
          </Typography>
        </Box>
        <IconButton size="small">
          {show ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      <Collapse in={show} timeout="auto" unmountOnExit>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 2, mt: 2 }}>
          {dogProfiles.map(dog => {
            const isAccount = dog.source === "account";
            const isDeleted = !!dog.deleted;

            return (
              <Paper
                key={dog.id}
                sx={{
                  p: 2, borderRadius: "12px",
                  border: `1px solid ${isDeleted ? COLORS.redBorder : COLORS.borderLight}`,
                  bgcolor: isDeleted ? "#FFF8F8" : QUIZ.color.white,
                  opacity: isDeleted ? 0.7 : 1,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: COLORS.brownDeep }}>{dog.name}</Typography>
                    <Chip
                      label={isAccount ? "Account" : "Subscription"}
                      size="small"
                      sx={{
                        bgcolor: isAccount ? COLORS.blueLight : COLORS.orangeLight,
                        color: isAccount ? COLORS.blue : COLORS.orangeText,
                        fontWeight: 600, fontSize: 10, height: 20,
                      }}
                    />
                    {isDeleted && (
                      <Chip
                        label="Deleted"
                        size="small"
                        sx={{ bgcolor: COLORS.redLight, color: COLORS.red, fontWeight: 600, fontSize: 10, height: 20 }}
                      />
                    )}
                  </Box>

                  {isAccount && (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {!isDeleted && onEditDog && (
                        <IconButton size="small" onClick={() => onEditDog(dog)} sx={{ color: COLORS.brown }}>
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                      {!isDeleted && onDeleteDog && (
                        <IconButton size="small" onClick={() => onDeleteDog(dog)} sx={{ color: COLORS.red }}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                      {isDeleted && onRestoreDog && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<RestoreIcon sx={{ fontSize: 14 }} />}
                          onClick={() => onRestoreDog(dog)}
                          sx={{ fontSize: 11, textTransform: "none" }}
                        >
                          Restore
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                  <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}><strong>Breed:</strong> {dog.breed || "-"}</Typography>
                  <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}><strong>Weight:</strong> {dog.weightKg ? `${dog.weightKg}kg` : "-"}</Typography>
                  <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}><strong>Daily:</strong> {dog.dailyGrams ? `${dog.dailyGrams}g` : "-"}</Typography>
                  <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>
                    <strong>Plan:</strong> {dog.selectedPlanDays ? `${dog.selectedPlanDays}d` : "-"} / {dog.selectedPackSize ? `${dog.selectedPackSize}g` : "-"}
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Collapse>
    </QuizStyledCard>
  );
}
